import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import OrderEditorForm from '@/routes/orders/OrderEditor.form'
import { orderEditorRouteLoader } from '@/routes/orders/OrderEditor.route'
import { OrderEditorProps } from '@/routes/orders/OrderEditor.types'
import { StatoOrdine } from '@/routes/orders/order.types'
import { Suspense, useRef } from 'react'
import { useFetcher, useParams } from 'react-router-dom'
import { Await, useLoaderData } from 'react-router-typesafe'
import { Form as DXForm, FormRef, Tab, TabbedItem, TabPanelOptions } from 'devextreme-react/form'
import AttachmentsManager from '@/components/file-manager/AttachmentsManager'
import DocumentUploader from '@/components/document/DocumentUploader'
import OrderEditorTasksGrid from '@/routes/orders/OrderEditor.tasks'
import { FileManagerRef } from 'devextreme-react/file-manager'
import { isUserRoleAllowed } from '@/routes/utils'
import { Roles } from '@/auth/azure/Roles'
import { DocumentPermissions } from '@/enums'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import './OrderEditor.scss'
import OrderEditorInvoicingStepsGrid from '@/routes/orders/OrderEditor.invoicingsteps'
import notify from 'devextreme/ui/notify'

const OrderEditor = (props: OrderEditorProps) => {
  const { creating, isReseller } = props
  const {
    title,
    order,
    getStati,
    getAziende,
    getOfferte,
    getCommesse,
    getStatiAcconto,
    getStatiFatturazione,
    userInfo,
    defaultCRUDAllowedRoles,
  } = useLoaderData<typeof orderEditorRouteLoader>()

  const { clientId } = useParams()
  const { client, service } = useQsAdminApiManager()

  const fetcher = useFetcher()
  const documentoGetRef = useRef<any>(null)
  const fileManagerRef = useRef<FileManagerRef>(null)
  const formRef = useRef<FormRef>(null)
  const isUpdatingFromGrid = useRef(false)
  const canCreateConsuntivoAsOrganizer =
    creating && order.consuntivo === true && userInfo?.roles?.hasRole(Roles.Organizer) === true
  const isOrderFormRoleAllowed =
    isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles) || canCreateConsuntivoAsOrganizer

  return (
    <>
      <h2 className={clientId ? '' : 'content-block'}>{title}</h2>
      <Suspense fallback={<LoadingPanel />}>
        <Await
          resolve={Promise.all([getStati, getAziende, getOfferte, getCommesse, getStatiAcconto, getStatiFatturazione])}
        >
          {([stati, aziende, offerte, commesse, stati_acconto, stati_fatturazione]) => (
            <>
              <div
                id="editor-order-container"
                className={clientId ? 'dx-card responsive-paddings' : 'content-block dx-card responsive-paddings'}
              >
                <fetcher.Form>
                  <OrderEditorForm
                    creating={creating}
                    isReseller={isReseller}
                    order={order}
                    stati={stati.data.value}
                    aziende={aziende.data.value}
                    offerte={offerte?.data.value}
                    commesse={commesse?.data.value}
                    stati_acconto={stati_acconto.data.value}
                    stati_fatturazione={stati_fatturazione.data.value}
                    isRoleAllowed={isOrderFormRoleAllowed}
                    userInfo={userInfo}
                    fetcher={fetcher}
                    formRef={formRef}
                    isUpdatingFromGrid={isUpdatingFromGrid}
                  />
                </fetcher.Form>
                {!creating && (
                  <div id="info-order-container">
                    <DXForm id="form-info">
                      <TabbedItem>
                        <TabPanelOptions
                          selectedIndex={isUserRoleAllowed(userInfo?.roles, [Roles.Administrator]) ? 0 : 1}
                        />
                        <Tab title="Punti di fatturazione">
                          <OrderEditorInvoicingStepsGrid
                            order={order}
                            creating={creating}
                            userInfo={userInfo}
                            readOnly={
                              !isUserRoleAllowed(userInfo?.roles, [
                                Roles.GlobalAdministrator,
                                Roles.Administrator,
                                Roles.Supervisor,
                              ])
                            }
                            showTitle={false}
                            onGridSaved={async () => {
                              const form = formRef.current?.instance()
                              form?.getEditor('fatturazione')?.option('readOnly', true)
                              try {
                                const response = await service.ordine(order.id).query((builder) => {
                                  builder.select('id', 'fatturazione', 'stato', 'punti_fatturazione_ordine')
                                  builder.expanding('fatturazione', (billingBuilder) => {
                                    billingBuilder.select('id', 'nome')
                                  })
                                  builder.expanding('stato', (statusBuilder) => {
                                    statusBuilder.select('id', 'nome')
                                  })
                                  builder.expanding('punti_fatturazione_ordine', (stepsBuilder) => {
                                    stepsBuilder.select('id')
                                  })
                                })
                                const savedOrder = response.data
                                isUpdatingFromGrid.current = true
                                try {
                                  form?.updateData('fatturazione', savedOrder.fatturazione)
                                } finally {
                                  isUpdatingFromGrid.current = false
                                }
                                form
                                  ?.getEditor('fatturazione')
                                  ?.option(
                                    'readOnly',
                                    savedOrder.stato?.nome === StatoOrdine.ANNULLATO ||
                                      !isUserRoleAllowed(userInfo?.roles, [
                                        Roles.GlobalAdministrator,
                                        Roles.Administrator,
                                      ]) ||
                                      (savedOrder.punti_fatturazione_ordine?.length ?? 0) > 0,
                                  )
                              } catch (error) {
                                console.error('Errore aggiornamento fatturazione ordine', error)
                                notify(
                                  'Punti salvati, ma impossibile aggiornare lo stato di fatturazione. Aggiorna la pagina.',
                                  'error',
                                  5000,
                                )
                              }
                            }}
                          ></OrderEditorInvoicingStepsGrid>
                        </Tab>
                        <Tab title="Task">
                          <OrderEditorTasksGrid
                            order={order}
                            creating={creating}
                            userInfo={userInfo}
                            readOnly={
                              !isUserRoleAllowed(userInfo?.roles, [
                                Roles.GlobalAdministrator,
                                Roles.Administrator,
                                Roles.Supervisor,
                                Roles.Organizer,
                              ])
                            }
                            showTitle={false}
                          ></OrderEditorTasksGrid>
                        </Tab>
                      </TabbedItem>
                    </DXForm>
                  </div>
                )}
              </div>
              {!creating && (
                <div
                  id="allegati-order-container"
                  className={clientId ? 'dx-card responsive-paddings' : 'content-block dx-card responsive-paddings'}
                >
                  <DXForm id="form-allegati">
                    <TabbedItem>
                      <Tab title="Documento">
                        <DocumentUploader
                          paramName={'orderfile'}
                          allowedExtensions={['.pdf']}
                          fileUrl={order.mainDocumentUrl}
                          uploadUrl={`${import.meta.env.VITE_QSADMINAPI_HOST}/api/ordine/upload?orderId=${order.id}`}
                          documentGetRef={documentoGetRef}
                          preliminarPermissionsAction={async () => {
                            try {
                              const response = await client.post(
                                '/api/ordine/drivepermissions',
                                {
                                  objectId: order.id,
                                  path: order.mainDocumentUrl,
                                },
                                {
                                  headers: {
                                    'Content-Type': 'application/json',
                                  },
                                },
                              )
                              return response.status === 200
                                ? (response.data as DocumentPermissions)
                                : DocumentPermissions.Deny
                            } catch (error) {
                              console.log('Errore in settaggio permessi sul documento ordine', error)
                              return DocumentPermissions.Deny
                            }
                          }}
                        />
                      </Tab>
                      <Tab title="Allegati">
                        <AttachmentsManager
                          fileManagerRef={fileManagerRef}
                          folderUrl={order.attachmentsFolderUrl}
                          preliminarPermissionsAction={async () => {
                            try {
                              const response = await client.post(
                                '/api/ordine/drivepermissions',
                                {
                                  objectId: order.id,
                                  path: order.attachmentsFolderUrl,
                                },
                                {
                                  headers: {
                                    'Content-Type': 'application/json',
                                  },
                                },
                              )
                              return response.status === 200
                                ? (response.data as DocumentPermissions)
                                : DocumentPermissions.Deny
                            } catch (error) {
                              console.log('Errore in settaggio permessi sulla cartella allegati ordine', error)
                              return DocumentPermissions.Deny
                            }
                          }}
                          createDirectoryAction={async (
                            rootFolderPath: string,
                            name: string,
                            conflictBehavior: string,
                          ) => {
                            return await client.post(
                              '/api/ordine/createfolder',
                              {
                                rootFolder: rootFolderPath,
                                newFolderName: name,
                                conflictBehavior,
                                objectId: order.id,
                              },
                              {
                                headers: {
                                  'Content-Type': 'application/json',
                                },
                              },
                            )
                          }}
                        />
                      </Tab>
                    </TabbedItem>
                  </DXForm>
                </div>
              )}
            </>
          )}
        </Await>
      </Suspense>
    </>
  )
}

export default OrderEditor
